import { DeviceProtocol } from '@hid/device/device_model'
import { MagnetDriver } from './../device'
import { DataDeadZone, postMagnetKeyInfo } from '@libs/types/response'
import { Fe87v2Devices, KK, VirtualIrokFe87V2IMC_24g, VirtualMer68SESelfDev } from '@hid/define/define'
import { KeyCode, KeyFunction, OutputMode, SetMagnetSenior, VK } from '@hid/define/define'
import { MagnetSeniorType } from '@libs/types/enum'
import { computeTravelsWithDeadzone, computeTravelsWithDeadzoneGet, computeTravelsWithDeadzoneGetForMer68SelfDev, joinBytes, splitIntoBytes } from './../tool'
import { allKeysForDevice, kkFromVK } from '@hid/define/define_fn'
import { fe87v2Driver } from '../fe87v2'
import { Mu68Handle } from '../magnet_handler'
import { delay, makeData64WithCRC } from '@libs/utils/tools'
import { adjustableKeys } from '@libs/define/adjustable_keys'
import { deviceManager } from '@hid/device/device_manager'

// 读 写
export enum ReadWrite {
  Read = 0x00,
  Write = 0x01,
}
let shouldStop = false
enum calibrateType {
  start = 0x0c,
  end = 0x0d,
}
enum datatype {
  bit_8 = 0x01,
  bit_16 = 0x02,
}
enum matrix6_12 {
  hall = 0x02,
  flux = 0x06,
}
export class Fe87V2Magnet extends MagnetDriver {
  private receive = 0
  private collectDataList = [
    ...this.collectData(matrix6_12.hall), // 行程
    ...this.collectData(matrix6_12.flux), // 磁通量
  ]
  private handlers: Mu68Handle[] = []
  constructor(
    private DeviceHID: DeviceProtocol,
    private parent: fe87v2Driver,
  ) {
    super()
    this.handlers = [new Mu68Handle()]
    this.DeviceHID.onReciveAny((data) => {
      this.handlers.forEach((handler) => {
        this.receive++
        if (shouldStop) return
        handler.handle(data)
      })
    })
  }

  private stopLoopAsk() {
    shouldStop = true
  }
  private startLoopAsk() {
    shouldStop = false
  }

  async startCalibration() {
    await this.parent.musicIns.musicCapturePause()
    this.toggleCalibration(calibrateType.start)
  }
  async endCalibration() {
    this.toggleCalibration(calibrateType.end)
    await this.parent.musicIns.musicCaptureContinue()
  }
  async startTravels() {
    await this.parent.musicIns.musicCapturePause()
    this.startLoopAsk()
    this.loopAsk()
  }
  async endTravels() {
    this.stopLoopAsk()
    this.handlers.forEach((handler) => {
      handler.clear()
    })
    await this.parent.musicIns.musicCaptureContinue()
  }

  private async toggleCalibration(type: calibrateType): Promise<number[]> {
    const data = makeData64WithCRC([0x5c, 0x03, 0x00, 0x00, type, 0xff, 0xff])
    const res = await this.DeviceHID.send(data)
    if (res[4] != 0x00) {
      throw Error(`${type == calibrateType.start ? 'start' : 'end'} calibration failed: 0x${res[4].toString(16)}`)
    }
    return res
  }
  private async loopAsk() {
    for (let time = 0; true; time++) {
      if (shouldStop) {
        return
      }
      if ([VirtualIrokFe87V2IMC_24g].includes(deviceManager.deviceID)) {
        return
      }
      for (let index = 0; index < this.collectDataList.length; index++) {
        if (shouldStop) {
          return
        }
        const item = this.collectDataList[index]
        await this.DeviceHID.send(item)
        //  TODO: 此处不应该判断是否成功
        // 当按住"0" 时开始行程校准，res[4] 的数据不是0x00
        // if (res[4] != 0) {
        //     throw new Error(`ask failed:${res[4]}`);
        // }
        while (this.receive < 3) {
          await delay(5)
        }
        this.receive = 0
      }
    }
  }

  private collectData(type: matrix6_12): number[][] {
    return [this.createDataPacket(type, datatype.bit_8), this.createDataPacket(type, datatype.bit_16)]
  }

  private createDataPacket(type: matrix6_12, datatype: datatype): number[] {
    return makeData64WithCRC([0x5c, 0x04, 0x12, 0x00, type, datatype, 0xff, 0xff])
  }

  private getAdjustableKeys() {
    return adjustableKeys(deviceManager.deviceID) as KK[]
  }

  private getDeviceKeys(): KK[] {
    if (Fe87v2Devices.includes(deviceManager.deviceID)) {
      return this.getAdjustableKeys()
    }
    if (deviceManager.deviceID == VirtualMer68SESelfDev) {
      return allKeysForDevice(deviceManager.deviceID)
    }
    return []
  }

  private filterKeys(keys: KK[]) {
    if (Fe87v2Devices.includes(deviceManager.deviceID)) {
      const deviceKeys = this.getAdjustableKeys()
      return keys.filter((key) => deviceKeys.includes(key))
    }
    return keys
  }

  async getKeysTravels(keys?: KK[]): Promise<postMagnetKeyInfo[]> {
    await this.parent.musicIns.musicCapturePause()

    if (!keys) {
      keys = this.getDeviceKeys()
    }
    keys = this.filterKeys(keys)
    const data1 = await this.parent.cmdKey(0x23, this.parent.makeCMDSelectArg(keys, 0x04))
    const dataTypes = await this.parent.cmdKey(0x23, this.parent.makeCMDSelectArg(keys, 0x08)) //按键的行程类型,RT需要为2
    const respMap = new Map<KK, postMagnetKeyInfo>()
    keys.forEach((key) => {
      if (!dataTypes.has(key)) return
      const one = {
        Key: key,
        KeyType: 0,
        PressNum: 0,
        ReleaseNum: 0,
        QuickTriggerPressNum: 0,
        QuickTriggerReleaseNum: 0,
      }
      const dt = dataTypes.get(key)![0]
      one.KeyType = dt % 0x10
      if (data1.has(key)) {
        one.PressNum = (data1.get(key)![1] << 8) + data1.get(key)![0]
        one.ReleaseNum = (data1.get(key)![1] << 8) + data1.get(key)![0]
      }
      if (deviceManager.deviceID == VirtualMer68SESelfDev) {
        const travel = this.parent.save.MagnetTravel.get(key)
        if (travel) {
          one.QuickTriggerPressNum = travel.QuickTriggerPressNum
          one.QuickTriggerReleaseNum = travel.QuickTriggerReleaseNum
        }
      }
      respMap.set(key, one)
    })
    const resp = Array.from(respMap.values())
    const resp2 = computeTravelsWithDeadzoneGetForMer68SelfDev(resp, this.parent.save.MagnetTravel, this.parent.save.DeadZone)
    await this.parent.musicIns.musicCaptureContinue()
    return resp2
  }

  async resetTravels(dataKeyType: Map<KK, [number, number]>) {
    const data = new Map<KK, number[]>()
    dataKeyType.forEach((value, key) => {
      const fnk = value[0] % 0x10
      data.set(key, [0x08, fnk, 0x00])
    })
    await this.parent.cmdKey(0x23, data, true)
  }

  async syncKeysTravels() {
    await this.parent.musicIns.musicCapturePause()
    const keys = this.getDeviceKeys()
    const dataKeyType = await this.parent.cmdKey(0x23, this.parent.makeCMDSelectArg(keys, 0x08))

    // if (this.parent.changeProfileFlag) {
    //   await this.resetTravels(dataKeyType)
    // }

    const magnetTravel = computeTravelsWithDeadzone(this.parent.save.MagnetTravel, this.parent.save.DeadZone)

    const data1 = new Map<KK, number[]>() //普通行程

    magnetTravel.forEach((value, key) => {
      if (!dataKeyType.has(key)) return
      data1.set(key, [0x04, value.PressNum & 0xff, (value.PressNum >> 8) & 0xff])
    })
    await this.parent.cmdKey(0x23, data1, true)

    if (deviceManager.deviceID == VirtualMer68SESelfDev) {
      const data2 = new Map<KK, number[]>()
      const data3 = new Map<KK, number[]>()

      magnetTravel.forEach((value, key) => {
        if (!dataKeyType.has(key)) return
        const dt = dataKeyType.get(key)![0]
        const fnk = dt % 0x10

        if (value.QuickTriggerReleaseNum == 0 && value.QuickTriggerPressNum == 0) {
          data3.set(key, [0x08, 0x10 + fnk, 0x00])
        } else {
          data2.set(key, [0x08, 0x20 + fnk, 0x00, key, 0x14, value.QuickTriggerPressNum & 0xff, (value.QuickTriggerPressNum >> 8) & 0xff, key, 0x15, value.QuickTriggerReleaseNum & 0xff, (value.QuickTriggerReleaseNum >> 8) & 0xff])
        }
      })

      await this.parent.cmdKey(0x23, data2, true)
      await this.parent.cmdKey(0x23, data3, true)
    }

    await this.parent.musicIns.musicCaptureContinue()
  }

  async syncSeniorkey() {
    await this.parent.musicIns.musicCapturePause()
    const dksData: { [index: KK]: SetMagnetSenior } = {}
    const mtData: { [index: KK]: SetMagnetSenior } = {}
    const tglData: { [index: KK]: SetMagnetSenior } = {}
    const mptData: { [index: KK]: SetMagnetSenior } = {}
    const endData: { [index: KK]: SetMagnetSenior } = {}
    const kmeData: { [index: KK]: SetMagnetSenior } = {}
    const rsData: { [index: KK]: SetMagnetSenior } = {}

    this.parent.save.Keys.forEach(async (value) => {
      if (value.Output.Mode == OutputMode.Magnet) {
        const magnetData = this.parent.save.MagnetData.get(value.Output.DataIndex)
        if (magnetData) {
          switch (magnetData.KeyType) {
            case MagnetSeniorType.SeniorKeyTypeDKS:
              dksData[value.Input.KK] = magnetData
              break
            case MagnetSeniorType.SeniorKeyTypeMT:
              mtData[value.Input.KK] = magnetData
              break
            case MagnetSeniorType.SeniorKeyTypeTGL:
              tglData[value.Input.KK] = magnetData
              break
            // case MagnetSeniorType.SeniorKeyTypeMPT:
            //     mptData[value.Input.KK] = magnetData
            //     break;
            // case MagnetSeniorType.SeniorKeyTypeEND:
            //     endData[value.Input.KK] = magnetData
            //     break;
            case MagnetSeniorType.SeniorKeyTypeKME:
              kmeData[value.Input.KK] = magnetData
              break
            case MagnetSeniorType.SeniorKeyTypeRS:
              rsData[value.Input.KK] = magnetData
          }
        }
      }
    })
    if (Object.keys(dksData).length > 0) {
      await this.setDKS(dksData)
    }
    if (Object.keys(mtData).length > 0) {
      await this.setMT(mtData)
    }
    if (Object.keys(tglData).length > 0) {
      await this.setTGL(tglData)
    }
    if (Object.keys(mptData).length > 0) {
      await this.setMPT(mptData)
    }
    if (Object.keys(endData).length > 0) {
      await this.setEND(endData)
    }
    if (Object.keys(kmeData).length > 0) {
      await this.setKME(kmeData)
    }
    if (Object.keys(rsData).length > 0) {
      await this.setRS(rsData)
    }
    await this.clearKmeBind()
    await this.parent.musicIns.musicCaptureContinue()
  }

  async clearKmeBind() {
    const keys = this.parent.resetKmeKeys
    const kks = keys.map((item) => item.Input.KK)
    const keysType = await this.parent.cmdKey(0x23, this.parent.makeCMDSelectArg(kks, 0x08))
    const resetSenior = new Map<KK, number[]>()

    console.log('clearKmeBind', keys)
    keys.forEach((value) => {
      const travel = keysType.get(value.Input.KK)![0] & 0b11110000
      resetSenior.set(value.Input.KK, [8, travel, 0])
    })

    if (resetSenior.size > 0) await this.parent.cmdKey(0x23, resetSenior, true)
    this.parent.clearKmeKeys()
  }

  private async setDKS(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]

    for (let index = 0; index < keys.length; index++) {
      const key = keys[index] as KK
      const value = data[key]

      let keyParam = new Array(10).fill(0x00)

      for (let index = 0; index < value.Keys.length; index++) {
        const magenetValue = value.Keys[index]
        if (magenetValue.KeyCode.length > 0) {
          keyParam[0 + index] = this.getMagenetKeyCode(magenetValue.KeyCode)
          keyParam[4 + index] = this.setTrPSx(magenetValue.KeyFunction)
        }
      }

      const [lowByte, hightByte] = splitIntoBytes(value.Keys[0].Travel * 100)
      keyParam[8] = lowByte
      keyParam[9] = hightByte

      group.set(key, keyParam)
    }
    this.parent.cmdKey(0x26, group, true)
  }

  private async setMT(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]

      const keyParam = new Array(3).fill(0xff)
      keyParam[0] = this.getMagenetKeyCode(value.Keys[0].KeyCode)
      keyParam[1] = this.getMagenetKeyCode(value.Keys[1].KeyCode)
      keyParam[2] = value.MTDuration
      group.set(key, keyParam)
    }
    this.parent.cmdKey(0x24, group, true)
  }

  private async setTGL(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]

    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]

      const keyParam = new Array(2).fill(0xff)
      keyParam[0] = this.getMagenetKeyCode(value.Keys[0].KeyCode)
      //keyParam[1] = value.MTDuration
      keyParam[1] = 0x14
      group.set(key, keyParam)
    }
    this.parent.cmdKey(0x25, group, true)
  }

  private async setMPT(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]

    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]

      const keyParam = new Array(9).fill(0xff)
      for (let index = 0; index < value.Keys.length; index++) {
        keyParam[0 + index] = this.getMagenetKeyCode(value.Keys[index].KeyCode)

        keyParam[3 + index * 2] = value.Keys[0].Travel & 0xff
        keyParam[3 + index * 2 + 1] = (value.Keys[0].Travel >> 8) & 0xff
      }
      group.set(key, keyParam)
    }
    this.parent.cmdKey(0x27, group, true)
  }

  private async setEND(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]
      group.set(key, [this.getMagenetKeyCode(value.Keys[0].KeyCode)])
    }
    this.parent.cmdKey(0x28, group, true)
  }

  private async setKME(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]
      const setValue = this.getMagenetKeyCode(value.Keys[0].KeyCode)
      group.set(key, [setValue])
    }
    await this.parent.cmdKey(0x2c, group, true)
  }

  private async setRS(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]
      const setValue = this.getMagenetKeyCode(value.Keys[0].KeyCode)
      group.set(key, [setValue])
    }
    this.parent.cmdKey(0x2d, group, true)
  }

  private getMagenetKeyCode(data: KeyCode) {
    if (data.length > 0 && data[0] > 0) {
      const code = kkFromVK(data[0] as VK, deviceManager.deviceID)
      if (code != undefined) {
        return code
      }
      throw new Error('未找到对应的按键值')
    }
    return 0xff
  }

  private setTrPSx(data: KeyFunction) {
    let res = 0
    for (let index = 0; index < data.length; index++) {
      if (index == 0) {
        if (data[index] == 1) {
          res |= 0x01 << 0
        }
        if (data[index] == 2) {
          res |= 0x01 << 1
        }
      } else if (index == 3) {
        if (data[index] > 0) {
          res |= 0x01 << 7
        }
      } else if (index == 1) {
        if (data[index] == 1) {
          res |= 0x01 << 2
        }
        if (data[index] == 2) {
          res |= 0x01 << 2
          res |= 0x01 << 3
          res |= 0x01 << 4
        }
        if (data[index] == 3) {
          res |= 0x01 << 2
        }
      } else if (index == 2) {
        if (data[index] == 1) {
          res |= 0x01 << 5
        }
        if (data[index] == 2 || data[index] == 3) {
          res |= 0x01 << 5
          res |= 0x01 << 6
        }
      }
    }
    return res
  }

  //[顶部死区, 底部死区]
  //写入时, 如果传-1, 则不写入
  async readOrSetDeadZone(isWrite: boolean, value?: [number, number]): Promise<[number, number]> {
    console.log(isWrite, value)
    return [0, 0]
  }

  private SetTopDeadZone = 0x16
  private SetBottomDeadZone = 0x17
  async getKeysDeadZone(keys?: KK[]): Promise<Map<KK, DataDeadZone>> {
    await this.parent.musicIns.musicCapturePause()
    if (!keys) {
      keys = this.getDeviceKeys()
    }
    keys = this.filterKeys(keys)
    const getTop = new Map<KK, number[]>()
    const getBottom = new Map<KK, number[]>()
    keys.forEach((key) => {
      getTop.set(key, [this.SetTopDeadZone, 0xff, 0xff])
      getBottom.set(key, [this.SetBottomDeadZone, 0xff, 0xff])
    })
    const topData = await this.parent.cmdKey(0x23, getTop, false)
    const bottomData = await this.parent.cmdKey(0x23, getBottom, false)

    let res = new Map<KK, DataDeadZone>()
    keys.forEach((key) => {
      let keyTopArr = topData.get(key)
      let keyBottomArr = bottomData.get(key)
      if (key == 0 && !keyTopArr && !keyBottomArr) {
        keyTopArr = [0x00, 0x00]
        keyBottomArr = [0x00, 0x00]
      }
      if (keyTopArr == undefined || keyBottomArr == undefined) {
        throw new Error('keyTopDeadZone or keyBottomDeadZone is undefined')
      }
      const keyTop = joinBytes([keyTopArr[1], keyTopArr[0]])
      const keyBottom = joinBytes([keyBottomArr[1], keyBottomArr[0]])
      res.set(key, { Top: keyTop, Bottom: keyBottom })
    })
    await this.parent.musicIns.musicCaptureContinue()
    return res
  }

  async syncKeysDeadZone() {
    await this.parent.musicIns.musicCapturePause()
    const deadZoneList = this.parent.save.DeadZoneList

    const setTopMap = new Map<KK, number[]>()
    const setBottomMap = new Map<KK, number[]>()

    const keys = this.getDeviceKeys()

    deadZoneList.forEach((value, key) => {
      if (keys.includes(key)) {
        setTopMap.set(key, [this.SetTopDeadZone, value.Top & 0xff, value.Top >> 8])
        setBottomMap.set(key, [this.SetBottomDeadZone, value.Bottom & 0xff, value.Bottom >> 8])
      }
    })
    await this.parent.cmdKey(0x23, setTopMap, true)
    await this.parent.cmdKey(0x23, setBottomMap, true)
    await this.parent.musicIns.musicCaptureContinue()
  }

  //测试用
  async DemoDebug() {
    console.log('debug:', 'end')
  }

  destroy() {}
}
